package com.steamstreet.vegasful.browser.components

import kotlinx.browser.window
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.events.Event

typealias Dispose = () -> Unit

/**
 * Adjusts the height of an HTMLTextAreaElement dynamically based on its content,
 * within the specified minimum and maximum row constraints.
 *
 * @param minRows The minimum number of rows the textarea should display. Defaults to 2.
 * @param maxRows The maximum number of rows the textarea is allowed to grow to. If null, there is no upper limit.
 * @return A Dispose function that removes the event listener when called, useful for cleanup.
 */
fun HTMLTextAreaElement.autoGrow(
    minRows: Int = 2,
    maxRows: Int? = null
): Dispose {
    val el = this
    // Base styles
    el.style.overflowY = "hidden"
    el.style.resize = "none"   // set "vertical" if you want manual resize too
    el.rows = minRows

    // Compute line height + chrome (padding + borders)
    val cs = window.getComputedStyle(el)
    val lineHeightPx = cs.lineHeight.removeSuffix("px").toDoubleOrNull()
        ?: (cs.fontSize.removeSuffix("px").toDoubleOrNull()?.times(1.2) ?: 20.0)
    val paddingTop = cs.paddingTop.removeSuffix("px").toDoubleOrNull() ?: 0.0
    val paddingBottom = cs.paddingBottom.removeSuffix("px").toDoubleOrNull() ?: 0.0
    val borderTop = cs.borderTopWidth.removeSuffix("px").toDoubleOrNull() ?: 0.0
    val borderBottom = cs.borderBottomWidth.removeSuffix("px").toDoubleOrNull() ?: 0.0
    val chrome = paddingTop + paddingBottom + borderTop + borderBottom

    val minHeight = (lineHeightPx * minRows + chrome)
    val maxHeight = maxRows?.let { (lineHeightPx * it + chrome) }

    fun recalc() {
        el.style.height = "auto" // allows shrink
        val target = el.scrollHeight.toDouble().coerceAtLeast(minHeight)
        val capped = maxHeight?.let { target.coerceAtMost(it) } ?: target
        el.style.height = "${capped}px"
        // Enable scrollbar only if we hit a hard max
        el.style.overflowY = if (maxHeight != null && target > (maxHeight + 0.5)) "auto" else "hidden"
    }

    val listener: (Event) -> Unit = { recalc() }
    el.addEventListener("input", listener)
    // Initial layout (also catches programmatic .value changes on the next tick)
    window.setTimeout({ recalc() }, 0)

    // Return a disposer to remove the listener when unmounting
    return { el.removeEventListener("input", listener) }
}